import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateOrganization } from 'api/adminOrganizations';
import { queryKeys } from 'constants/queryKeys';
import { organizationManagementStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TOrganization, TUpdateOrganizationRequest } from 'types';
import { CURRENT_USER_QUERY_KEY } from './useCurrentUser';
import { toErrorMessage } from './utils';

type UseUpdateOrganizationOptions = {
  onSuccess?: (organization: TOrganization) => void;
};

type UseUpdateOrganizationResult = {
  update: (organizationId: number, request: TUpdateOrganizationRequest) => void;
  isSubmitting: boolean;
  error: string | undefined;
};

export function useUpdateOrganization(
  options: UseUpdateOrganizationOptions = {},
): UseUpdateOrganizationResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = organizationManagementStrings.edit;

  const mutation = useMutation({
    mutationFn: async (params: {
      organizationId: number;
      request: TUpdateOrganizationRequest;
    }) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }

      return updateOrganization(params.organizationId, params.request);
    },
    onSuccess: (organization) => {
      if (!user) return;
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ORGANIZATIONS, user.uid],
      });
      // 自組織の利用機能・プランを変えたときにサイドバー等へ即時反映する
      queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
      options.onSuccess?.(organization);
    },
  });

  const update = (
    organizationId: number,
    request: TUpdateOrganizationRequest,
  ) => {
    mutation.mutate({ organizationId, request });
  };

  const error = mutation.error
    ? toErrorMessage(mutation.error, strings.errors.submitFailed)
    : undefined;

  return {
    update,
    isSubmitting: mutation.isPending,
    error,
  };
}
